import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Menu, X } from "lucide-react";

export default function Header({ variant = "full" }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Simplified header with just logo and return button
  if (variant === "simple") {
    return (
      <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300">
              <Building2 className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: '#3b82f6' }} />
              <span className="text-xl sm:text-2xl font-bold text-white">Top Prop</span>
            </a>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              size="sm"
              className="border-2 text-white border-white hover:bg-blue-600 hover:border-blue-600"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </header>
    );
  }

  // Full navigation header
  return (
    <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg" style={{ backgroundColor: '#1E1E1E' }}>
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300">
            <Building2 className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: '#3b82f6' }} />
            <span className="text-xl sm:text-2xl font-bold text-white">Top Prop</span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              onClick={() => navigate("/get-matched")}
              size="sm"
              className="text-white px-4 py-2 text-sm font-semibold shadow-md transition-all duration-300"
              style={{ 
                backgroundColor: '#3b82f6',
                borderRadius: '10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                e.currentTarget.style.color = '#000000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              data-testid="get-matched-button"
              title="Find a vetted property manager near you"
            >
              I Need Management
            </Button>
            <Button
              onClick={() => navigate("/company-signup")}
              size="sm"
              variant="outline"
              className="bg-white px-4 py-2 text-sm font-semibold shadow-md transition-all duration-300"
              style={{ 
                color: '#3b82f6',
                borderColor: '#3b82f6',
                borderWidth: '2px',
                borderRadius: '10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
                e.currentTarget.style.color = '#3b82f6';
              }}
              data-testid="company-signup-button"
              title="Sign up to receive local property owner leads"
            >
              I Offer Management
            </Button>
            <Button
              onClick={() => navigate("/whats-your-rent-worth")}
              size="sm"
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white px-3 py-2 text-sm font-semibold shadow-md transition-all duration-300"
              style={{ borderRadius: '10px' }}
              title="Find out your property's rental value"
            >
              Rent Worth?®
            </Button>
            <Button
              onClick={() => navigate("/property-manager-login")}
              variant="outline"
              size="sm"
              className="border-2 h-11 px-3 text-sm transition-all duration-300"
              style={{ 
                borderColor: '#FFFFFF',
                color: '#FFFFFF',
                borderRadius: '10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#FFFFFF';
              }}
              data-testid="pm-login-button"
            >
              Client Login
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 border-t border-slate-700 pt-4">
            <Button
              onClick={() => {
                navigate("/get-matched");
                setMobileMenuOpen(false);
              }}
              size="sm"
              className="w-full text-white font-semibold shadow-md transition-all duration-300"
              style={{ 
                backgroundColor: '#3b82f6',
                borderRadius: '10px'
              }}
            >
              I Need Management
            </Button>
            <Button
              onClick={() => {
                navigate("/company-signup");
                setMobileMenuOpen(false);
              }}
              size="sm"
              variant="outline"
              className="w-full bg-white font-semibold shadow-md transition-all duration-300"
              style={{ 
                color: '#3b82f6',
                borderColor: '#3b82f6',
                borderWidth: '2px',
                borderRadius: '10px'
              }}
            >
              I Offer Management
            </Button>
            <Button
              onClick={() => {
                navigate("/whats-your-rent-worth");
                setMobileMenuOpen(false);
              }}
              size="sm"
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white font-semibold shadow-md transition-all duration-300"
              style={{ borderRadius: '10px' }}
            >
              Rent Worth?®
            </Button>
            <Button
              onClick={() => {
                navigate("/property-manager-login");
                setMobileMenuOpen(false);
              }}
              variant="outline"
              size="sm"
              className="w-full border-2 font-semibold transition-all duration-300"
              style={{ 
                borderColor: '#FFFFFF',
                color: '#FFFFFF',
                borderRadius: '10px'
              }}
            >
              Client Login
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
